import {
  OVERLAY_ADD_PADDING_CLASSNAME,
  ScrollingDirection,
} from '@allesauto/utils/consts';
import clsx from 'clsx';

import { getWhiteLabelHeaderClassNames } from './funcs';
import { HeaderProps } from '../index';

export interface HeaderArgs extends HeaderProps {
  isStickyHeader?: boolean;
  scrollingDirection: ScrollingDirection;
  open: boolean;
  openMenu?: boolean;
}

export function getHeaderClassNames({
  context,
  size,
  isSimpleHeader,
  isStickyHeader,
  logoPosition,
  whiteLabel,
  open,
  scrollingDirection,
  openMenu,
}: HeaderArgs) {
  const headerSizeClassName = {
    height: clsx(
      size === 'large' ? 'h-[70px] md:h-100px' : 'h-60px md:h-[70px]'
    ),
    top: clsx(
      'lg:top-0',
      size === 'large'
        ? '-top-[70px] md:-top-100px'
        : '-top-60px md:-top-[70px]'
    ),
  };

  if (whiteLabel) {
    return {
      headerCartMenu:
        'h-full items-center sm:w-1/2 md:col-end-9 md:col-start-2 md:flex md:self-stretch md:w-1/3',
      iconClass: 'flex-shrink-0 !text-white relative top-[-10px]',
      navigationClasses: clsx(
        'fixed__color nav__color__darkblue ',
        openMenu && 'open'
      ),
      headerMenu: clsx(
        'hidden lg:flex lg:h-full lg:self-stretch',
        logoPosition === 'center'
          ? 'lg:col-end-9 lg:col-start-2'
          : 'lg:col-end-15 lg:col-start-8'
      ),
      headerClasses: clsx(
        '!top-0 darkblue header height-90 position-fixed',
        openMenu && 'menu-open'
      ),
      buttonClass: clsx(openMenu && 'open'),
    };
  }

  return {
    spacerClass: clsx(headerSizeClassName.height, 'bg-grey10'),
    headerClass: clsx(
      isStickyHeader && !isSimpleHeader ? 'fixed' : 'relative',
      headerSizeClassName.height,
      'bg-white top-0 w-full z-50',
      'duration-300 ease-linear transition-all lg:transition-none',
      isStickyHeader &&
        ((!scrollingDirection || scrollingDirection === 'up') && !open
          ? 'top-0'
          : headerSizeClassName.top),
      context !== 'navigation-with-stepper' && 'border-b-1px border-grey20',
      OVERLAY_ADD_PADDING_CLASSNAME
    ),
    gridClass: 'grid grid-cols-24 h-full items-center',
    headerMenu: clsx(
      'hidden lg:flex lg:h-full lg:self-stretch',
      logoPosition === 'center'
        ? 'lg:col-end-9 lg:col-start-2'
        : 'lg:col-end-15 lg:col-start-8'
    ),
    headerMenuToggle: clsx(
      'col-end-4 col-start-2 flex items-center self-stretch lg:hidden',
      logoPosition === 'left' && 'md:col-end-21 md:col-start-19'
    ),
    headerCartMenu:
      'h-full items-center sm:w-1/2 md:col-end-9 md:col-start-2 md:flex md:self-stretch md:w-1/3',
    headerUserMenu:
      'items-center w-1/3 sm:hidden md:col-end-9 md:col-start-2 md:flex md:h-full md:self-stretch',
    navClass:
      'h-full w-full max-w-screen-xl text-center ml-auto mr-auto duration-300 ease-linear transition-all',
    logoContainerClass: clsx(
      'col-end-18 col-start-7 flex justify-center',
      logoPosition === 'center'
        ? 'md:col-end-16 md:col-start-10'
        : 'md:col-end-8 md:col-start-2 md:justify-start md:row-start-1'
    ),
    logoClass: clsx(
      'cursor-pointer text-purple80 w-full monochrome:text-black',
      size === 'large'
        ? 'md:h-[60px] h-[40px]'
        : 'md:h-[22px] h-[12px] md:monochrome:h-[40px] monochrome:h-[22px]'
    ),
    //TODO: Remove justify-end when favorite is implemented
    iconsContainerClass:
      'sm:col-start-19 sm:col-end-25 pt-7px md:col-end-24 md:col-start-21 flex w-full items-center justify-self-start md:justify-self-end self-stretch justify-end',
    iconClass: 'flex-shrink-0 text-purple80 monochrome:text-black',
    darkBgClass: 'bg-black fixed h-full inset-0 w-full z-20 opacity-40',
    navigationClasses: clsx(
      'fixed__color nav__color__darkblue ',
      openMenu && 'open'
    ),
    headerClasses: clsx(
      'darkblue header height-90 position-fixed wl-header monochrome:min-[200px] ',
      openMenu && 'menu-open'
    ),
    buttonClass: clsx(openMenu && 'open'),
  };
}

export const getClassNamesForWLCartHeader = function (
  whiteLabel?: string | boolean
) {
  if (whiteLabel) {
    return getWhiteLabelHeaderClassNames(whiteLabel);
  }

  const className: Record<string, string> = {
    buttonContainerClass: 'flex pr-40px ',
    popoverButtonClass:
      'border-b-7px duration-200 ease-out flex font-normal font-regular items-center pt-px relative text-p1 text-purple80 transition-colors z-20 cursor-pointer h-full',
    popoverPanelClass:
      'absolute inset-x-0 top-[69px] bg-white w-[25.1%] flex left-[66.65%]',
    notificationClass:
      '!leading-5 absolute bg-red60 rounded-circle text-[12px] text-center text-white h-20px -right-[11px] w-20px lg:top-[10px] top-[6px] md:top-[12px]',
    gridClass: clsx('bg-grey90 grid grid-cols-24 relative top-0'),
    linkClass:
      'duration-300 font-regular no-underline text-p1 text-white transition-colors hover:text-grey50 overflow-hidden text-ellipsis whitespace-nowrap block',
    containerClass: clsx(
      '!-mb-7px border-b-7px border-transparent flex h-[calc(100%-7px)] m-0 relative w-1/3 wl-border-highlight hover:border-b-7px hover:border-green70'
    ),
    popoverClass: 'flex items-center p-0 h-full lg:pt-0 ml-auto justify-center',
    buttonClass: 'flex items-center p-0',
    popoverTitleClass:
      'font-headline font-weight-bold text-headline4 text-grey100 text-left',
  };

  const priceClasses: Record<string, string> = {
    wrapperClass: 'pb-20px',
    mainPriceClass: '!text-p2',
    monthlyPriceTextClass: clsx(
      '!max-w-full !pt-0 !text-p2',
      whiteLabel ? 'font-headline' : '!font-headline'
    ),
    monthlyPriceClass: '!text-p2',
    monthlyPriceWrapperClass: '!flex-col !items-start',
  };

  return { className, priceClasses };
};
